/**
 * Voice & Smart Intake API client — WO-24.
 *
 * Route: POST /api/sb/voice
 *
 * The app sends the transcript TEXT that was produced on-device (edge STT,
 * Gate Q5). Raw audio is NEVER sent to the backend — the backend does no
 * audio transcription (api-types 0.4.0, VoiceRouteRequest: transcript-only).
 *
 * Scope is derived from the auth token server-side; it is never in the request
 * body (mobile CLAUDE.md invariant 2 — no PII in URLs; scope from token).
 *
 * Response intent routing (handled by the caller, voice.tsx):
 *   ingest     → batchId returned; caller pushes to review queue.
 *   assistant  → assistantReply returned; caller shows inline.
 *   estimator  → draftId returned; caller navigates to estimator screen.
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { post } from './client';
import {
  VoiceRouteRequest,
  VoiceRouteResponse,
} from '@beam/api-types';
import type {
  VoiceIntent,
  VoiceRouteResponse as VoiceRouteResponseType,
} from '@beam/api-types';

// ---------------------------------------------------------------------------
// Voice route endpoint
// ---------------------------------------------------------------------------

export interface RouteVoiceOpts {
  /** Optional reference to a stored audio blob (object-storage id, NOT the audio itself). */
  audioAttachmentId?: string;
  /** Estimator: continue an existing draft instead of starting a new one. */
  draftId?: string;
  /** Assistant/estimator: the tenancy unit context; defaults to org scope when absent. */
  tenancyUnitId?: string;
}

/**
 * Send a transcript + intent to the backend voice router.
 *
 * The backend routes the request to the correct SmartBox call
 * (ingest / assistant.ask / estimator.turn) based on `intent` and returns a
 * `VoiceRouteResponse` whose fields depend on the resolved intent.
 *
 * @param transcript  The text produced by edge STT or manual entry — NOT audio.
 * @param intent      'ingest' | 'assistant' | 'estimator'
 * @param opts        Optional context fields (draftId, tenancyUnitId, audioAttachmentId).
 * @returns VoiceRouteResponse validated against the api-types 0.4.0 zod schema.
 */
export async function routeVoice(
  transcript: string,
  intent: VoiceIntent,
  opts: RouteVoiceOpts = {},
): Promise<VoiceRouteResponseType> {
  // Validate outbound shape against api-types to catch drift early.
  const body = VoiceRouteRequest.parse({
    transcript,
    intent,
    ...(opts.audioAttachmentId !== undefined && {
      audioAttachmentId: opts.audioAttachmentId,
    }),
    ...(opts.draftId !== undefined && { draftId: opts.draftId }),
    ...(opts.tenancyUnitId !== undefined && {
      tenancyUnitId: opts.tenancyUnitId,
    }),
  });

  const raw = await post<unknown>('/api/sb/voice', body);

  // Validate inbound shape — fails loudly on contract drift.
  return VoiceRouteResponse.parse(raw);
}
